// «Настройки → Шаблоны писем» (042: contracts/api.md, «Маршруты»; data-model.md; research Р2–Р4):
// список писем каталога по группам и общие фразы, страница письма с полями и метками, предпросмотр
// на учебных данных, сохранение с номером версии, возврат версии и история. Слова писем и метки —
// в каталоге модуля доставки (delivery/letters); контракт несёт только то, что видит экран.
import { z } from '../kernel/zod.ts';
import { PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { mailVariants } from '../delivery/message.ts';

/** Предел темы письма (FR-014): длиннее — предупреждение, не ошибка. */
export const LETTER_SUBJECT_LIMIT = 60;

/** Сколько знаков темы покажет телефон во входящих (ui.md: «Телефон покажет около 40 знаков»). */
export const LETTER_SUBJECT_PHONE = 40;

/** Предел длины одного поля текста письма. */
export const LETTER_FIELD_MAX = 2000;

/**
 * Номер записи каталога: письмо (`results`, `welcome`) или общая фраза (`phrase:help`).
 * Только латиница, цифры и «_» — номер идёт в путь запроса.
 */
export const LetterTemplateIdSchema = z
  .string()
  .regex(/^(phrase:)?[a-z][a-z0-9_]{0,47}$/, { error: 'Нет такого шаблона письма' });
export type LetterTemplateId = z.infer<typeof LetterTemplateIdSchema>;

export const LetterTemplateParamsSchema = z.object({ id: LetterTemplateIdSchema });

/** Группы списка (FR-050): «О задачах», «Руководителю и куратору», «Доступ и вход», «Служебные». */
export const letterGroups = ['tasks', 'copies', 'access', 'service'] as const;
export type LetterGroup = (typeof letterGroups)[number];
export const LetterGroupSchema = z.enum(letterGroups);

export const letterGroupLabels = {
  tasks: 'О задачах',
  copies: 'Руководителю и куратору',
  access: 'Доступ и вход',
  service: 'Служебные',
} as const satisfies Record<LetterGroup, string>;

/** Заголовок строки групп общих фраз в списке. */
export const letterPhrasesTitle = 'Общие фразы';

/** Кто и когда последним сохранил текст; null — «Как в системе». */
export const LetterChangedSchema = z.object({ at: TimestampSchema, by: PersonRefSchema });
export type LetterChanged = z.infer<typeof LetterChangedSchema>;

export const LetterTemplateItemSchema = z.object({
  id: LetterTemplateIdSchema,
  title: z.string(),
  /** Когда уходит письмо: «после рассылки итогов». */
  when: z.string(),
  /** Кому — словами: «ответственным». */
  who: z.string(),
  changed: LetterChangedSchema.nullable(),
  /** «В системе новый текст» (FR-056): умолчание каталога изменилось после правки. */
  catalogChanged: z.boolean(),
});
export type LetterTemplateItem = z.infer<typeof LetterTemplateItemSchema>;

export const LetterTemplateGroupSchema = z.object({
  id: LetterGroupSchema,
  title: z.string(),
  items: z.array(LetterTemplateItemSchema),
});

export const LetterPhraseItemSchema = z.object({
  id: LetterTemplateIdSchema,
  title: z.string(),
  /** Действующий текст фразы. */
  text: z.string(),
  /** Записи каталога, где стоит фраза: «В 11 письмах». */
  usedBy: z.array(LetterTemplateIdSchema),
  changed: LetterChangedSchema.nullable(),
  catalogChanged: z.boolean(),
});
export type LetterPhraseItem = z.infer<typeof LetterPhraseItemSchema>;

/** GET /api/letter-templates. */
export const LetterTemplateListSchema = z.object({
  groups: z.array(LetterTemplateGroupSchema),
  phrases: z.array(LetterPhraseItemSchema),
});
export type LetterTemplateList = z.infer<typeof LetterTemplateListSchema>;

/** Поле текста письма (FR-051): тема, предпросмотр, заголовок, абзацы; у общей фразы — `text`. */
export const LetterFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** Серая подсказка, что это поле в письме: «Серая строка после темы во входящих». */
  hint: z.string(),
  /** Действующий текст с метками `{Название совещания}`. */
  value: z.string(),
  /** Текст каталога — для «Как в системе» и «Отменить правки». */
  catalogValue: z.string(),
  multiline: z.boolean(),
  /** Предел знаков: у темы — 60 (предупреждение после него, FR-014). */
  maxLength: z.number().int().positive().nullable(),
});
export type LetterField = z.infer<typeof LetterFieldSchema>;

/** Метка данных записи (FR-052): имя, обязательная, пример значения на учебных данных. */
export const LetterMarkSchema = z.object({
  name: z.string(),
  required: z.boolean(),
  sample: z.string(),
});
export type LetterMark = z.infer<typeof LetterMarkSchema>;

/** Общая фраза письма — только чтение на странице письма (ui.md). */
export const LetterPhraseRefSchema = z.object({
  id: LetterTemplateIdSchema,
  title: z.string(),
  text: z.string(),
  /** В скольких письмах стоит фраза. */
  usedBy: z.number().int().nonnegative(),
});

/** GET /api/letter-templates/{id}; ответ сохранения и возврата. */
export const LetterTemplateSchema = z.object({
  id: LetterTemplateIdSchema,
  /** letter — письмо каталога; phrase — общая фраза. */
  kind: z.enum(['letter', 'phrase']),
  group: LetterGroupSchema.nullable(),
  title: z.string(),
  when: z.string(),
  who: z.string(),
  /** Номер действующей версии; 0 — правок ещё не было. Сохранение и возврат идут с ним. */
  version: z.number().int().min(0),
  changed: LetterChangedSchema.nullable(),
  catalogChanged: z.boolean(),
  fields: z.array(LetterFieldSchema),
  marks: z.array(LetterMarkSchema),
  /** Части письма, которые ставит система: «Кнопки „✓ Выполнено“ и „Новый срок“», «Код входа». */
  fixedBlocks: z.array(z.string()),
  phrases: z.array(LetterPhraseRefSchema),
});
export type LetterTemplate = z.infer<typeof LetterTemplateSchema>;

/** Черновик полей: ключ поля → текст с метками. */
export const LetterFieldsDraftSchema = z.record(z.string(), z.string().max(LETTER_FIELD_MAX));

export const letterPreviewWidths = [600, 360] as const;
export type LetterPreviewWidth = (typeof letterPreviewWidths)[number];

/** POST /api/letter-templates/{id}/preview — черновик не сохраняется и в журнал не идёт. */
export const LetterPreviewBodySchema = z.object({
  fields: LetterFieldsDraftSchema,
  variant: z.enum(mailVariants),
  width: z.union([z.literal(600), z.literal(360)]),
});
export type LetterPreviewBody = z.infer<typeof LetterPreviewBodySchema>;

/** Проблема поля черновика: код MAIL-025…027 и текст с меткой. */
export const LetterProblemSchema = z.object({
  field: z.string(),
  code: z.enum(['MAIL-025', 'MAIL-026', 'MAIL-027']),
  message: z.string(),
});
export type LetterProblem = z.infer<typeof LetterProblemSchema>;

export const LetterPreviewSchema = z.object({
  /** Имя отправителя во входящих: «Система «Контроль протоколов»». */
  sender: z.string(),
  subject: z.string(),
  subjectLength: z.number().int().nonnegative(),
  preheader: z.string(),
  html: z.string(),
  text: z.string(),
  problems: z.array(LetterProblemSchema),
});
export type LetterPreview = z.infer<typeof LetterPreviewSchema>;

/** PUT /api/letter-templates/{id}: версия, которую видел администратор, и поля. */
export const UpdateLetterTemplateBodySchema = z.object({
  version: z.number().int().min(0),
  fields: LetterFieldsDraftSchema,
});
export type UpdateLetterTemplateBody = z.infer<typeof UpdateLetterTemplateBodySchema>;

/** POST /api/letter-templates/{id}/restore: «Вернуть эту версию» или «Вернуть как в системе». */
export const RestoreLetterTemplateBodySchema = z.object({
  version: z.number().int().min(0),
  to: z.union([z.number().int().min(1), z.literal('default')]),
});
export type RestoreLetterTemplateBody = z.infer<typeof RestoreLetterTemplateBodySchema>;

/** Причина версии: правка, «Вернуть эту версию», «Вернуть как в системе». */
export const letterVersionReasons = ['edit', 'restore_version', 'restore_default'] as const;
export type LetterVersionReason = (typeof letterVersionReasons)[number];

export const letterVersionReasonLabels = {
  edit: 'Правка',
  restore_version: 'Возврат версии',
  restore_default: 'Возврат как в системе',
} as const satisfies Record<LetterVersionReason, string>;

export const LetterFieldChangeSchema = z.object({
  key: z.string(),
  label: z.string(),
  before: z.string(),
  after: z.string(),
});

export const LetterVersionSchema = z.object({
  version: z.number().int().min(1),
  at: TimestampSchema,
  by: PersonRefSchema,
  reason: z.enum(letterVersionReasons),
  restoredFrom: z.number().int().min(1).nullable(),
  /** Действующая версия. */
  current: z.boolean(),
  changes: z.array(LetterFieldChangeSchema),
});
export type LetterVersion = z.infer<typeof LetterVersionSchema>;

/** GET /api/letter-templates/{id}/history: новые сверху; последняя строка экрана — «Как в системе». */
export const LetterHistorySchema = z.object({
  versions: z.array(LetterVersionSchema),
  /** Версия сервиса, чьё умолчание действует при «Как в системе»: «0.34.0». */
  serviceVersion: z.string(),
});
export type LetterHistory = z.infer<typeof LetterHistorySchema>;
